import React, { useCallback } from 'react';
import {
  Form,
  TextInput,
  useCreate,
  useStore,
} from 'react-admin';
import { useWatch } from 'react-hook-form';

import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import Cancel from '@mui/icons-material/Cancel';

import ButtonGroupSelectInput from '../../../utils/ButtonGroupSelectInput';
import agentModes, { AgentMode } from '../../../../constants/agentModes';
import analysisTypes, { analysisDescription, AnalysisType, analysisTypeChoices } from '../../../../utilities/schemas/analysisTypes';

const AnalysisFormContent = ({
  hideBackButton,
}: {
  hideBackButton?: boolean;
}) => {
  const analysisType = useWatch({ name: 'analysisType' }) as AnalysisType;
  const [, setAgentMode] = useStore<AgentMode>('agentMode', agentModes.FETCHING);
  const theme = useTheme();

  return (
    <Stack gap={2}>
      <ButtonGroupSelectInput
        source="analysisType"
        defaultValue="investigation"
        choices={analysisTypeChoices}
      />
      <Box
        display="flex"
        flexDirection="column"
        gap={4}
      >
        {analysisType === analysisTypes.CUSTOM && (
          <TextInput
            source="customPrompt"
            multiline
            minRows={3}
            placeholder={analysisDescription[analysisType]}
            label={false}
            variant="outlined"
            helperText={false}
          />
        )}
        {analysisType !== analysisTypes.CUSTOM && (
          <Card
            variant="outlined"
            sx={{
              margin: 0,
              padding: 2,
              backgroundColor: theme.palette.background.grey,
            }}
          >
            <Typography>
              {analysisDescription[analysisType]}
            </Typography>
          </Card>
        )}
        <Box justifyContent="flex-end" display="flex" gap={2}>
          {!hideBackButton && (
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => setAgentMode(agentModes.VIEW)}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            startIcon={<Send />}
          >
            Generate analysis
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

const AiAnalysisForm = ({
  defaultValues,
  caseId,
  onSuccessStartAnalysis,
  hideBackButton,
}: {
  defaultValues: {
    analysisType?: AnalysisType;
    customPrompt?: string;
  };
  caseId: string;
  onSuccessStartAnalysis: () => void;
  hideBackButton?: boolean;
}) => {
  const [createAnalysis] = useCreate();

  const onSubmit = useCallback(async (formData: {
    analysisType?: AnalysisType;
    customPrompt?: string;
  }) => {
    await createAnalysis(
      `cases/${caseId}/analysis`,
      {
        data: {
          analysisType: formData.analysisType,
          customPrompt: formData.analysisType === analysisTypes.CUSTOM
            ? formData.customPrompt
            : undefined,
        },
      },
      {
        onSuccess: () => {
          onSuccessStartAnalysis();
        },
      },
    );
  }, [createAnalysis, caseId, onSuccessStartAnalysis]);

  return (
    <Form record={defaultValues} onSubmit={onSubmit}>
      <AnalysisFormContent
        hideBackButton={hideBackButton}
      />
    </Form>
  );
};

export default AiAnalysisForm;
